import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef,
  Optional,
  Self,
  HostListener,
  ContentChild,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { CdkOverlayOrigin, ScrollStrategy, Overlay } from '@angular/cdk/overlay';
import { ESCAPE, ENTER, SPACE, UP_ARROW, DOWN_ARROW, HOME, END, TAB } from '@angular/cdk/keycodes';
import {
  SgFormControlBase,
  subscriptAnimation,
  panelAnimation,
  groupCollapseAnimation,
  DROPDOWN_PANEL_POSITIONS,
} from '../../shared';
import {
  SgAutoCompleteSelect2Change,
  SgAutoCompleteSelect2QueryChange,
  SgAutoCompleteSelect2OptionContext,
  SgAutoCompleteSelect2TriggerContext,
  SgAutoCompleteSelect2OptionGroup,
  SgAutoCompleteSelect2GroupBy,
} from './sg-auto-complete-select-2.models';
import {
  SgAutoCompleteSelect2OptionDirective,
  SgAutoCompleteSelect2TriggerDirective,
  SgAutoCompleteSelect2AsyncTemplateDirective,
} from './sg-auto-complete-select-2.directives';

/** Represents a navigable item in the dropdown (either a group header or an option) */
interface NavigableItem<T> {
  type: 'group-header' | 'option';
  group?: SgAutoCompleteSelect2OptionGroup<T>;
  option?: T;
}

@Component({
  selector: 'sg-auto-complete-select-2',
  templateUrl: './sg-auto-complete-select-2.component.html',
  styleUrls: ['./sg-auto-complete-select-2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [subscriptAnimation, panelAnimation, groupCollapseAnimation],
  standalone: false,
})
export class SgAutoCompleteSelect2Component<T = any> extends SgFormControlBase<T | T[]> implements OnChanges {
  static ngAcceptInputType_multiple: BooleanInput;
  static ngAcceptInputType_hideInput: BooleanInput;

  @ViewChild('trigger', { static: true }) triggerElement!: ElementRef<HTMLDivElement>;
  @ViewChild('searchInput') searchInputElement?: ElementRef<HTMLInputElement>;
  @ViewChild(CdkOverlayOrigin, { static: true }) overlayOrigin!: CdkOverlayOrigin;
  @ViewChild('optionsList') optionsListElement?: ElementRef<HTMLDivElement>;

  @ContentChild(SgAutoCompleteSelect2OptionDirective) optionDirective?: SgAutoCompleteSelect2OptionDirective;
  @ContentChild(SgAutoCompleteSelect2TriggerDirective) triggerDirective?: SgAutoCompleteSelect2TriggerDirective;
  @ContentChild(SgAutoCompleteSelect2AsyncTemplateDirective)
  asyncTemplateDirective?: SgAutoCompleteSelect2AsyncTemplateDirective;

  @Input() placeholder: string = '';
  @Input() searchInputPlaceholder: string = 'Search...';
  @Input() size: 'default' | 'small' = 'default';
  @Input() variant: 'default' | 'secondary' | 'underline' = 'default';
  @Input() panelClass: string | string[] = '';

  /** Display mode for multi-select: 'count' shows "X selected", 'chips' shows stacked chips UI */
  @Input() multiSelectDisplay: 'count' | 'chips' = 'count';

  /** Panel width mode: 'trigger' matches trigger width, 'auto' fits content (min = trigger width) */
  @Input() panelWidth: 'trigger' | 'auto' = 'trigger';

  @Input() options: T[] = [];

  /**
   * Maximum number of items to display in the dropdown panel.
   * If not specified or 0, all items are displayed.
   */
  @Input() maxDisplayItems?: number;

  /** Property path to use as the display label for options */
  @Input() labelKey: string = 'label';

  /** Property path to use as the unique value for options */
  @Input() valueKey: string = 'value';

  /** Function to transform option to its value */
  @Input() valueTransform?: (option: T) => any;

  /** Property name for unique identification (used for trackBy) */
  @Input() trackBy?: string;

  /** Function to group options */
  @Input() groupBy?: SgAutoCompleteSelect2GroupBy<T>;

  /** Function to compare option values. Defaults to comparing by valueKey when both are objects. */
  @Input() compareWith: (o1: any, o2: any) => boolean = (o1, o2) => this.defaultCompareWith(o1, o2);

  @Input()
  get multiple(): boolean {
    return this._multiple;
  }
  set multiple(value: BooleanInput) {
    this._multiple = coerceBooleanProperty(value);
    this.cdr.markForCheck();
  }
  private _multiple = false;

  @Input()
  get hideInput(): boolean {
    return this._hideInput;
  }
  set hideInput(value: BooleanInput) {
    this._hideInput = coerceBooleanProperty(value);
    this.cdr.markForCheck();
  }
  private _hideInput = false;

  /**
   * Controls when value changes are emitted in multi-select mode.
   * - true (default): Emit valueChange immediately on each checkbox click
   * - false: Show Apply/Clear buttons, emit only when Apply is clicked
   */
  @Input() instantApply: boolean = true;

  @Output() readonly valueChange = new EventEmitter<T | T[]>();
  @Output() readonly selectionChange = new EventEmitter<SgAutoCompleteSelect2Change<T>>();
  @Output() readonly searchQueryChange = new EventEmitter<SgAutoCompleteSelect2QueryChange>();
  @Output() readonly openedChange = new EventEmitter<boolean>();
  @Output() readonly scrollEnd = new EventEmitter<void>();

  isOpen = false;
  highlightedIndex = -1;
  searchQuery = '';
  groupAnimationDisabled = true;

  readonly positions = DROPDOWN_PANEL_POSITIONS;
  readonly overlayPanelClass = 'sg-auto-complete-select-overlay-pane';
  scrollStrategy: ScrollStrategy;

  private _groupedOptionsCache: SgAutoCompleteSelect2OptionGroup<T>[] | null = null;
  private _groupExpandedState: Map<string, boolean> = new Map();
  private _triggerContextCache: SgAutoCompleteSelect2TriggerContext<T> | null = null;
  private _optionContextCache: Map<T, SgAutoCompleteSelect2OptionContext<T>> = new Map();

  /** Pending value when instantApply is false (deferred mode) */
  private pendingValue: T | T[] | null = null;
  /** Tracks if there are unsaved changes in deferred mode */
  hasPendingChanges: boolean = false;

  constructor(
    @Optional() @Self() override ngControl: NgControl | null,
    private overlay: Overlay
  ) {
    super(ngControl);
    this.scrollStrategy = this.overlay.scrollStrategies.reposition();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this._groupedOptionsCache = null;
      this._optionContextCache.clear();
      this._triggerContextCache = null;
      this.cdr.markForCheck();
    }
  }

  get triggerWidth(): number {
    return this.triggerElement?.nativeElement?.offsetWidth || 200;
  }

  get panelClasses(): string[] {
    const classes = [this.overlayPanelClass];
    if (Array.isArray(this.panelClass)) {
      classes.push(...this.panelClass.filter((c) => c));
    } else if (this.panelClass) {
      classes.push(this.panelClass);
    }
    if (this.panelWidth === 'auto') {
      classes.push('sg-auto-complete-select-overlay-pane--auto-width');
    }

    return classes;
  }

  get empty(): boolean {
    if (this._multiple) {
      return !this.value || (Array.isArray(this.value) && this.value.length === 0);
    }

    return this.value === null || this.value === undefined;
  }

  get isGrouped(): boolean {
    return !!this.groupBy;
  }

  get groupedOptions(): SgAutoCompleteSelect2OptionGroup<T>[] {
    if (this._groupedOptionsCache !== null) {
      return this._groupedOptionsCache;
    }

    const optionsToDisplay = this.options;

    if (!this.isGrouped) {
      this._groupedOptionsCache = [{ key: '', label: '', options: optionsToDisplay, expanded: true }];

      return this._groupedOptionsCache;
    }

    const groups = new Map<string, T[]>();
    const groupOrder: string[] = [];

    for (const option of optionsToDisplay) {
      const groupKey = this.groupBy!(option);
      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
        groupOrder.push(groupKey);
      }
      groups.get(groupKey)!.push(option);
    }

    this._groupedOptionsCache = groupOrder.map((key) => ({
      key,
      label: this.groupBy?.labels?.[key] || key,
      options: groups.get(key)!,
      expanded: this._groupExpandedState.get(key) ?? true,
    }));

    return this._groupedOptionsCache;
  }

  get flattenedOptions(): T[] {
    return this.options;
  }

  getOptionLabel(option: T): string {
    if (typeof option === 'string') {
      return option;
    }

    return (option as any)[this.labelKey] ?? String(option);
  }

  getOptionsMaxHeight(): string | null {
    if (!this.maxDisplayItems) return null;
    const rowHeight = this._multiple ? 36 : 32;
    // Multiple: +1 row for the "Select all" header; single: standard item height
    // const px = this._multiple ? (this.maxDisplayItems + 1) * 36 : this.maxDisplayItems * 32;
    const px = (this.maxDisplayItems + (this._multiple ? 1 : 0)) * rowHeight;
    return `${px}px`;
  }

  getOptionValue(option: T): any {
    if (this.valueTransform) {
      return this.valueTransform(option);
    }

    if (typeof option === 'string' || typeof option === 'number') {
      return option;
    }

    return (option as any)[this.valueKey] ?? option;
  }

  getOptionTrackBy(option: T): any {
    if (this.trackBy && typeof option === 'object' && option !== null) {
      return (option as any)[this.trackBy];
    }

    return this.getOptionValue(option);
  }

  get displayValue(): string {
    if (this.empty) {
      return '';
    }

    if (this._multiple && Array.isArray(this.value)) {
      const selectedOptions = this.options.filter((opt) =>
        (this.value as any[]).some((v) => this.compareWith(v, this.getOptionValue(opt)))
      );

      return selectedOptions.map((opt) => this.getOptionLabel(opt)).join(', ');
    }

    // Normalize: if parent passes an array for a single-select binding, use the first element
    const singleValue = Array.isArray(this.value) ? ((this.value as any[])[0] ?? null) : this.value;
    const selectedOption = this.options.find((opt) => this.compareWith(singleValue, this.getOptionValue(opt)));

    return selectedOption ? this.getOptionLabel(selectedOption) : '';
  }

  get selectedCount(): number {
    // In deferred mode with pending changes, count against pendingValue
    if (!this.instantApply && this.hasPendingChanges) {
      if (!this._multiple || !Array.isArray(this.pendingValue)) {
        return 0;
      }
      return this.pendingValue.length;
    }

    // Default behavior: count against current value
    if (!this._multiple || !Array.isArray(this.value)) {
      return 0;
    }

    return this.value.length;
  }

  /**
   * Gets the last selected option for chips display mode
   */
  get lastSelectedOption(): { label: string; value: any } | null {
    if (!this._multiple || !Array.isArray(this.value) || this.value.length === 0) {
      return null;
    }

    const lastValue = this.value[this.value.length - 1];
    const option = this.options.find((opt) => this.compareWith(lastValue, this.getOptionValue(opt)));

    if (option) {
      return { label: this.getOptionLabel(option), value: this.getOptionValue(option) };
    }

    return null;
  }

  /**
   * Gets the count of additional selected items (excluding the last one) for chips display
   */
  get additionalSelectedCount(): number {
    return Math.max(0, this.selectedCount - 1);
  }

  /**
   * Gets all selected options for chips display mode
   */
  get selectedOptions(): { label: string; value: any }[] {
    if (!this._multiple || !Array.isArray(this.value)) {
      return [];
    }

    return this.options
      .filter((opt) => (this.value as any[]).some((v) => this.compareWith(v, this.getOptionValue(opt))))
      .map((opt) => ({ label: this.getOptionLabel(opt), value: this.getOptionValue(opt) }));
  }

  /**
   * Gets a comma-separated list of all selected option labels for tooltip display
   */
  get selectedLabelsTooltip(): string {
    return this.selectedOptions.map((opt) => opt.label).join(', ');
  }

  get selectAllState(): 'none' | 'all' | 'indeterminate' {
    if (!this._multiple) {
      return 'none';
    }

    const totalOptions = this.options.length;
    const selectedCount = this.selectedCount;

    if (selectedCount === 0) {
      return 'none';
    }

    if (selectedCount === totalOptions) {
      return 'all';
    }

    return 'indeterminate';
  }

  get selectAllIcon(): string {
    switch (this.selectAllState) {
      case 'all':
        return 'check_box';
      case 'indeterminate':
        return 'indeterminate_check_box';
      default:
        return 'check_box_outline_blank';
    }
  }

  /**
   * Returns a flat list of navigable items including group headers (when grouped) and options.
   * Used for keyboard navigation to allow focusing on group headers.
   */
  get navigableItems(): NavigableItem<T>[] {
    const items: NavigableItem<T>[] = [];

    for (const group of this.groupedOptions) {
      // Add group header as navigable item if grouped and has a label
      if (this.isGrouped && group.label) {
        items.push({ type: 'group-header', group });
      }

      // Add options only if group is expanded
      if (group.expanded !== false) {
        for (const option of group.options) {
          items.push({ type: 'option', option, group });
        }
      }
    }

    return items;
  }

  /**
   * Returns the currently highlighted navigable item
   */
  get highlightedItem(): NavigableItem<T> | null {
    if (this.highlightedIndex < 0 || this.highlightedIndex >= this.navigableItems.length) {
      return null;
    }

    return this.navigableItems[this.highlightedIndex];
  }

  /**
   * Checks if a group header is currently highlighted
   */
  isGroupHighlighted(group: SgAutoCompleteSelect2OptionGroup<T>): boolean {
    const item = this.highlightedItem;

    return item?.type === 'group-header' && item.group?.key === group.key;
  }

  /**
   * Gets the navigable index for an option
   */
  getNavigableIndex(groupIndex: number, optionIndex: number): number {
    const items = this.navigableItems;
    const groups = this.groupedOptions;
    const targetGroup = groups[groupIndex];
    const targetOption = targetGroup?.options[optionIndex];

    if (!targetOption) {
      return -1;
    }

    return items.findIndex((item) => item.type === 'option' && item.option === targetOption);
  }

  /**
   * Gets the navigable index for a group header
   */
  getGroupNavigableIndex(group: SgAutoCompleteSelect2OptionGroup<T>): number {
    return this.navigableItems.findIndex((item) => item.type === 'group-header' && item.group?.key === group.key);
  }

  get triggerContext(): SgAutoCompleteSelect2TriggerContext<T> {
    if (this._triggerContextCache) {
      return this._triggerContextCache;
    }

    const selectedOptions = this.options.filter((opt) => this.isSelected(opt));

    this._triggerContextCache = {
      $implicit: this.value as T | T[],
      option: this.value as T | T[],
      options: selectedOptions.map((opt) => ({
        value: opt,
        selected: true,
        multiple: this._multiple,
        viewValue: this.getOptionLabel(opt),
      })),
    };

    return this._triggerContextCache;
  }

  getOptionContext(option: T): SgAutoCompleteSelect2OptionContext<T> {
    const cached = this._optionContextCache.get(option);
    if (cached && cached.selected === this.isSelected(option)) {
      return cached;
    }

    const selected = this.isSelected(option);
    const viewValue = this.getOptionLabel(option);

    const context: SgAutoCompleteSelect2OptionContext<T> = {
      $implicit: {
        option,
        selected,
        multiple: this._multiple,
        viewValue,
      },
      option,
      selected,
      multiple: this._multiple,
      viewValue,
    };

    this._optionContextCache.set(option, context);

    return context;
  }

  toggle(): void {
    if (this.disabled) {
      return;
    }

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    if (this.disabled || this.isOpen) {
      return;
    }

    this.isOpen = true;
    this.highlightedIndex = this.getInitialHighlightedIndex();

    // In deferred mode, initialize pending value with current value
    if (!this.instantApply) {
      this.pendingValue = this._multiple && Array.isArray(this.value) ? [...this.value] : this.value;
      this.hasPendingChanges = false;
    }

    // Always reset search and notify parent to show all options
    this.searchQuery = '';
    this._groupedOptionsCache = null;
    this.searchQueryChange.emit({ query: '' });

    this.openedChange.emit(true);
    this.cdr.markForCheck();

    setTimeout(() => {
      this.searchInputElement?.nativeElement?.focus();
    }, 50);
  }

  close(): void {
    if (!this.isOpen) {
      return;
    }

    this.isOpen = false;
    this.highlightedIndex = -1;
    this.searchQuery = '';
    this._groupedOptionsCache = null;
    this._touched = true;
    this.onTouched();
    this.openedChange.emit(false);
    this.cdr.markForCheck();
  }

  onBackdropClick(): void {
    // In deferred mode, discard pending changes when clicking outside
    if (!this.instantApply && this.hasPendingChanges) {
      this.discardPendingChanges();
    }
    this.close();
  }

  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery = input.value;
    this._groupedOptionsCache = null;
    this.highlightedIndex = 0;
    this.searchQueryChange.emit({ query: this.searchQuery });
    this.cdr.markForCheck();
  }

  onSearchInputKeydown(event: KeyboardEvent): void {
    const keyCode = event.keyCode;

    // Handle navigation keys directly since the overlay is outside the component's DOM tree
    // and events won't bubble to the @HostListener
    if (
      keyCode === ESCAPE ||
      keyCode === ENTER ||
      keyCode === UP_ARROW ||
      keyCode === DOWN_ARROW ||
      keyCode === HOME ||
      keyCode === END ||
      keyCode === TAB
    ) {
      this.onKeydown(event);
    }
  }

  onOptionsScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const threshold = 50;

    if (element.scrollHeight - element.scrollTop - element.clientHeight < threshold) {
      this.scrollEnd.emit();
    }
  }

  selectOption(option: T): void {
    if (this._multiple) {
      this.toggleMultipleOption(option);
    } else {
      this.selectSingleOption(option);
    }
  }

  isSelected(option: T): boolean {
    const optionValue = this.getOptionValue(option);

    // In deferred mode with pending changes, check against pendingValue
    if (!this.instantApply && this.hasPendingChanges) {
      if (this._multiple && Array.isArray(this.pendingValue)) {
        return this.pendingValue.some((v) => this.compareWith(v, optionValue));
      }
      return this.pendingValue != null && this.compareWith(this.pendingValue, optionValue);
    }

    // Default behavior: check against current value
    if (this._multiple && Array.isArray(this.value)) {
      return this.value.some((v) => this.compareWith(v, optionValue));
    }

    // Normalize: if parent passes an array for a single-select binding, use the first element
    const singleValue = Array.isArray(this.value) ? ((this.value as any[])[0] ?? null) : this.value;
    return singleValue != null && this.compareWith(singleValue, optionValue);
  }

  clear(event?: MouseEvent): void {
    event?.stopPropagation();
    this.value = this._multiple ? [] : null;
    this._triggerContextCache = null;
    this.onChange(this.value);
    this.valueChange.emit(this.value as T | T[]);
    this.cdr.markForCheck();
  }

  /**
   * Clears all selected items except the last one (used by stacked chip X button)
   */
  clearAllButLast(event: MouseEvent): void {
    event.stopPropagation();

    if (!this._multiple || !Array.isArray(this.value) || this.value.length <= 1) {
      return;
    }

    const lastValue = this.value[this.value.length - 1];
    this.value = [lastValue];
    this._triggerContextCache = null;
    this.onChange(this.value);
    this.valueChange.emit(this.value);
    this.cdr.markForCheck();
  }

  /**
   * Removes a specific chip/option from the selection
   */
  removeChip(option: { label: string; value: any }, event: MouseEvent): void {
    event.stopPropagation();

    if (!this._multiple || !Array.isArray(this.value)) {
      return;
    }

    const currentValue = [...this.value];
    const index = currentValue.findIndex((v) => this.compareWith(v, option.value));

    if (index >= 0) {
      currentValue.splice(index, 1);
      this.value = currentValue;
      this._triggerContextCache = null;
      this.onChange(this.value);
      this.valueChange.emit(this.value);
      this.cdr.markForCheck();
    }
  }

  /**
   * Applies pending changes in deferred mode (emits valueChange and closes dropdown)
   */
  applyChanges(): void {
    if (this.hasPendingChanges && this.pendingValue !== null) {
      this.value = this.pendingValue;
      this._triggerContextCache = null;
      this.onChange(this.value);
      this.valueChange.emit(this.value);
      this.hasPendingChanges = false;
      this.close();
    }
  }

  /**
   * Clears all pending selections and applies the change (emits and closes dropdown)
   */
  clearPendingChanges(): void {
    const clearedValue = this._multiple ? [] : null;
    this.value = clearedValue;
    this._triggerContextCache = null;
    this.onChange(this.value);
    this.valueChange.emit(this.value);
    this.hasPendingChanges = false;
    this.pendingValue = null;
    this.close();
  }

  /**
   * Discards pending changes without applying them
   */
  discardPendingChanges(): void {
    this.pendingValue = null;
    this.hasPendingChanges = false;
  }

  toggleSelectAll(): void {
    if (!this._multiple) {
      return;
    }

    const newValue = this.selectAllState === 'all' ? [] : this.options.map((opt) => this.getOptionValue(opt));

    if (this.instantApply) {
      // Instant mode: Emit immediately (current behavior)
      this.value = newValue;
      this._triggerContextCache = null;
      this.onChange(this.value);
      this.valueChange.emit(this.value);
    } else {
      // Deferred mode: Update pending value only
      this.pendingValue = newValue;
      this.hasPendingChanges = true;
    }

    this.cdr.markForCheck();
  }

  toggleGroup(group: SgAutoCompleteSelect2OptionGroup<T>): void {
    // Enable animation first and force change detection so Angular sees it before the DOM change
    this.groupAnimationDisabled = false;
    this.cdr.detectChanges();

    // Now update the group state - animation will be active for this change
    const newExpanded = !group.expanded;
    this._groupExpandedState.set(group.key, newExpanded);
    // Directly mutate the cached group to preserve DOM elements for animation
    group.expanded = newExpanded;
    this.cdr.markForCheck();

    // Disable animation after it completes (animation duration is ~200ms)
    setTimeout(() => {
      this.groupAnimationDisabled = true;
    }, 250);
  }

  selectGroup(group: SgAutoCompleteSelect2OptionGroup<T>): void {
    if (!this._multiple) {
      return;
    }

    const groupValues = group.options.map((opt) => this.getOptionValue(opt));
    const sourceValues = this.instantApply
      ? Array.isArray(this.value)
        ? [...this.value]
        : []
      : Array.isArray(this.pendingValue)
        ? [...this.pendingValue]
        : [];
    const allSelected = groupValues.every((gv) => sourceValues.some((cv) => this.compareWith(cv, gv)));

    let newValue: T[];
    if (allSelected) {
      newValue = sourceValues.filter((cv) => !groupValues.some((gv) => this.compareWith(cv, gv)));
    } else {
      newValue = [...sourceValues];
      for (const gv of groupValues) {
        if (!newValue.some((cv) => this.compareWith(cv, gv))) {
          newValue.push(gv);
        }
      }
    }

    if (this.instantApply) {
      // Instant mode: Emit immediately (current behavior)
      this.value = newValue;
      this._triggerContextCache = null;
      this.onChange(this.value);
      this.valueChange.emit(this.value);
    } else {
      // Deferred mode: Update pending value only
      this.pendingValue = newValue;
      this.hasPendingChanges = true;
    }

    this.cdr.markForCheck();
  }

  isGroupSelected(group: SgAutoCompleteSelect2OptionGroup<T>): 'none' | 'all' | 'indeterminate' {
    // In deferred mode with pending changes, check against pendingValue
    const sourceValue = !this.instantApply && this.hasPendingChanges ? this.pendingValue : this.value;

    if (!this._multiple || !Array.isArray(sourceValue)) {
      return 'none';
    }

    const currentValue = sourceValue as T[];
    const groupValues = group.options.map((opt) => this.getOptionValue(opt));
    const selectedCount = groupValues.filter((gv) => currentValue.some((cv: any) => this.compareWith(cv, gv))).length;

    if (selectedCount === 0) {
      return 'none';
    }

    if (selectedCount === groupValues.length) {
      return 'all';
    }

    return 'indeterminate';
  }

  getGroupCheckboxIcon(group: SgAutoCompleteSelect2OptionGroup<T>): string {
    switch (this.isGroupSelected(group)) {
      case 'all':
        return 'check_box';
      case 'indeterminate':
        return 'indeterminate_check_box';
      default:
        return 'check_box_outline_blank';
    }
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (this.disabled) {
      return;
    }

    const keyCode = event.keyCode;

    if (!this.isOpen) {
      if (keyCode === ENTER || keyCode === SPACE || keyCode === DOWN_ARROW) {
        event.preventDefault();
        this.open();
      }

      return;
    }

    switch (keyCode) {
      case ESCAPE:
        event.preventDefault();
        this.close();
        break;

      case TAB:
        this.close();
        break;

      case ENTER:
        event.preventDefault();
        if (this.highlightedIndex >= 0) {
          const highlightedItem = this.highlightedItem;
          if (highlightedItem?.type === 'group-header' && highlightedItem.group) {
            this.toggleGroup(highlightedItem.group);
          } else if (highlightedItem?.type === 'option' && highlightedItem.option) {
            this.selectOption(highlightedItem.option);
          }
        }
        break;

      case UP_ARROW:
        event.preventDefault();
        this.highlightPrevious();
        break;

      case DOWN_ARROW:
        event.preventDefault();
        this.highlightNext();
        break;

      case HOME:
        event.preventDefault();
        this.highlightFirst();
        break;

      case END:
        event.preventDefault();
        this.highlightLast();
        break;
    }
  }

  writeValue(value: T | T[] | null): void {
    this._value = value as (T | T[] | null) & (T | null);
    this._triggerContextCache = null;
    this.cdr.markForCheck();
  }

  getOptionFlatIndex(groupIndex: number, optionIndex: number): number {
    if (!this.isGrouped) {
      return optionIndex;
    }

    let flatIndex = 0;
    const groups = this.groupedOptions;

    for (let i = 0; i < groupIndex; i++) {
      if (groups[i].expanded !== false) {
        flatIndex += groups[i].options.length;
      }
    }

    return flatIndex + optionIndex;
  }

  private selectSingleOption(option: T): void {
    const optionValue = this.getOptionValue(option);
    this.value = optionValue;
    this._triggerContextCache = null;
    this.onChange(this.value);
    this.valueChange.emit(this.value);
    this.selectionChange.emit({ value: optionValue, option });
    this.cdr.markForCheck();
    this.close();
  }

  private toggleMultipleOption(option: T): void {
    const optionValue = this.getOptionValue(option);

    if (this.instantApply) {
      // Instant mode: Emit immediately (current behavior)
      const currentValue = Array.isArray(this.value) ? [...this.value] : [];
      const index = currentValue.findIndex((v) => this.compareWith(v, optionValue));

      if (index >= 0) {
        currentValue.splice(index, 1);
      } else {
        currentValue.push(optionValue);
      }

      this.value = currentValue;
      this._triggerContextCache = null;
      this.onChange(this.value);
      this.valueChange.emit(this.value);
      this.selectionChange.emit({ value: optionValue, option });
    } else {
      // Deferred mode: Update pending value only
      const currentPending = Array.isArray(this.pendingValue) ? [...this.pendingValue] : [];
      const index = currentPending.findIndex((v) => this.compareWith(v, optionValue));

      if (index >= 0) {
        currentPending.splice(index, 1);
      } else {
        currentPending.push(optionValue);
      }

      this.pendingValue = currentPending;
      this.hasPendingChanges = true;
    }

    this.cdr.markForCheck();
  }

  private getInitialHighlightedIndex(): number {
    const items = this.navigableItems;

    if (this.empty) {
      return items.length > 0 ? 0 : -1;
    }

    // Find the first selected option in navigable items
    if (this._multiple && Array.isArray(this.value) && this.value.length > 0) {
      const firstSelectedValue = this.value[0];

      return items.findIndex(
        (item) =>
          item.type === 'option' &&
          item.option &&
          this.compareWith(this.getOptionValue(item.option), firstSelectedValue)
      );
    }

    return items.findIndex(
      (item) => item.type === 'option' && item.option && this.compareWith(this.getOptionValue(item.option), this.value)
    );
  }

  private highlightNext(): void {
    const items = this.navigableItems;
    if (items.length === 0) {
      return;
    }

    this.highlightedIndex = (this.highlightedIndex + 1) % items.length;
    this.scrollToHighlighted();
    this.cdr.markForCheck();
  }

  private highlightPrevious(): void {
    const items = this.navigableItems;
    if (items.length === 0) {
      return;
    }

    this.highlightedIndex = this.highlightedIndex <= 0 ? items.length - 1 : this.highlightedIndex - 1;
    this.scrollToHighlighted();
    this.cdr.markForCheck();
  }

  private highlightFirst(): void {
    this.highlightedIndex = 0;
    this.scrollToHighlighted();
    this.cdr.markForCheck();
  }

  private highlightLast(): void {
    const items = this.navigableItems;
    this.highlightedIndex = items.length - 1;
    this.scrollToHighlighted();
    this.cdr.markForCheck();
  }

  private scrollToHighlighted(): void {
    requestAnimationFrame(() => {
      const listElement = this.optionsListElement?.nativeElement;
      if (!listElement) {
        return;
      }

      // Try to find highlighted option or group header
      const highlightedElement = listElement.querySelector(
        '.sg-auto-complete-select__option--highlighted, .sg-auto-complete-select__group-header--highlighted'
      ) as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  /**
   * Default comparison function that handles both primitive and object values.
   * When both values are objects with the valueKey property, compares by that property.
   * Otherwise falls back to strict equality.
   */
  private defaultCompareWith(o1: any, o2: any): boolean {
    if (o1 === o2) {
      return true;
    }

    if (o1 == null || o2 == null) {
      return false;
    }

    // If both are objects and have the valueKey property, compare by it
    if (typeof o1 === 'object' && typeof o2 === 'object') {
      const v1 = o1[this.valueKey];
      const v2 = o2[this.valueKey];
      if (v1 !== undefined && v2 !== undefined) {
        return v1 === v2;
      }
    }

    // If one is primitive and one is object with valueKey, compare the primitive to the valueKey
    if (typeof o1 === 'object' && typeof o2 !== 'object') {
      return o1[this.valueKey] === o2;
    }
    if (typeof o1 !== 'object' && typeof o2 === 'object') {
      return o1 === o2[this.valueKey];
    }

    return false;
  }
}
